import {StrictMode} from 'react';
import {createRoot} from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { SiteContentProvider } from './contexts/SiteContentContext';
import { AuthProvider } from './lib/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { NexoraXLedgerProvider } from './lib/NexoraXLedgerManager.tsx';

if (typeof window !== 'undefined') {
  // Global garbage-collection protection for browser SpeechSynthesisUtterance
  // This solves Chrome, Safari, and Edge bugs where speaking long-form sentences
  // triggers silent garbage collection, leaving onend event uncalled and mic stuck
  if ('speechSynthesis' in window) {
    const OriginalSpeak = window.speechSynthesis.speak;
    (window as any)._activeUtterances = (window as any)._activeUtterances || [];
    
    window.speechSynthesis.speak = function (utterance: SpeechSynthesisUtterance) {
      if (utterance) {
        (window as any)._activeUtterances.push(utterance);
        
        const originalOnEnd = utterance.onend;
        const originalOnError = utterance.onerror;
        
        utterance.onend = function (event) {
          (window as any)._activeUtterances = (window as any)._activeUtterances.filter((u: any) => u !== utterance);
          if (originalOnEnd) {
            originalOnEnd.call(this, event);
          }
        };
        
        utterance.onerror = function (event) {
          (window as any)._activeUtterances = (window as any)._activeUtterances.filter((u: any) => u !== utterance);
          if (originalOnError) {
            originalOnError.call(this, event);
          }
        };
      }
      return OriginalSpeak.call(window.speechSynthesis, utterance);
    };
  }

  // Persistent definition for toggling background active canvas views
  (window as any).switchActiveCanvasRoute = function (targetCanvasElementId: string) {
    console.log(`Nexorax Matrix Sync: Shifting background active canvas view to viewport layer: [${targetCanvasElementId}]`);
    
    // 1. Invalidate and hide all canvas wrapper layout blocks
    const activeViewportsList = document.querySelectorAll('.nx-canvas-container-box');
    activeViewportsList.forEach(viewport => {
        (viewport as HTMLElement).style.display = 'none';
    });
    
    // 2. Clear visual tab button focus selections
    const tabButtonsList = document.querySelectorAll('.canvas-tab-btn');
    tabButtonsList.forEach(button => {
        button.classList.remove('active');
    });
    
    // 3. Reveal target selection panel box
    const targetElement = document.getElementById(targetCanvasElementId);
    if (targetElement) {
        targetElement.style.display = 'block';
    }
    
    // 4. Assign active focus highlight to current clicked tab button
    if (window.event && (window.event as any).currentTarget) {
        (window.event as any).currentTarget.classList.add('active');
    }
  };

  // Administrative monitoring nodes controller and visibility checker
  const runSecurityCloakingCheck = () => {
    // 1. Evaluate administrative clearance from secure session memory strings
    const isAdminAuthenticated = sessionStorage.getItem('admin session verification') === 'verified_admin_active';
    
    // Target administrative node wrappers across your homepage layout layout
    const tickerBar   = document.getElementById("nexorax-NexoraX-ledger-ticker");
    const capitalFlow = document.getElementById("capital-flow-box");
    const healthBox   = document.getElementById("health-box");
    const alignmentBox = document.getElementById("ledger-alignment-active");
    const adminNavBtn = document.getElementById("admin-panel-nav-link");

    const currentLoggedState = (window as any)._lastAdminStateLogged;
    const nextState = isAdminAuthenticated ? 'admin' : 'guest';

    if (!isAdminAuthenticated) {
        if (currentLoggedState !== nextState) {
            console.log(">> [NEXORAX SENTRY]: Public guest session verified. Enforcing Admin UI cloaking protocols...");
            (window as any)._lastAdminStateLogged = nextState;
        }
        
        // 2. Forcefully hide nodes from public view DOM to prevent extraction or inspecting without breaking React
        if (tickerBar)   tickerBar.style.display = "none";
        if (capitalFlow) capitalFlow.style.display = "none";
        if (healthBox)   healthBox.style.display = "none";
        if (alignmentBox) alignmentBox.style.display = "none";
        
        // 3. Hide the Admin Panel tab link from the main navigation menu header bar
        if (adminNavBtn)  adminNavBtn.style.display = "none";
        
    } else {
        if (currentLoggedState !== nextState) {
            console.log(">> [NEXORAX SENTRY]: Admin credentials authenticated. Injecting metrics to Admin Panel views.");
            (window as any)._lastAdminStateLogged = nextState;
        }
        // Code here handles appending these metric panels exclusively inside the active admin panel view container
        (window as any).injectAdminMetricsToNestedView();
    }
  };

  (window as any).injectAdminMetricsToNestedView = function() {
    const adminPanelHealthTab = document.getElementById("admin-system-state-view");
    if (adminPanelHealthTab) {
        // Prevents double appending
        if (!document.getElementById("nested-capital-flow")) {
            // Appends the layout blocks as high-fidelity inline widgets inside the protected view
            adminPanelHealthTab.innerHTML += `
                <div class="admin-nested-row flex gap-4 mt-4 p-4 border border-slate-900 bg-[#030712]/50 rounded-xl" id="admin-nested-metrics-row">
                    <div id="nested-capital-flow" class="flex-1 text-xs font-mono text-cyan-400">Capital Flow Data Matrix Connected...</div>
                    <div id="nested-health-core" class="flex-1 text-xs font-mono text-emerald-405">Health Status Matrix Verified...</div>
                </div>
            `;
        }
    }
  };

  // Run immediately and listen to DOMContentLoaded / state shifts
  document.addEventListener("DOMContentLoaded", runSecurityCloakingCheck);
  window.addEventListener("load", runSecurityCloakingCheck);
  // Also expose on window to be manually invocable during admin logins
  (window as any).runSecurityCloakingCheck = runSecurityCloakingCheck;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <SiteContentProvider>
      <AuthProvider>
        <ToastProvider>
          <NexoraXLedgerProvider>
            <App />
          </NexoraXLedgerProvider>
        </ToastProvider>
      </AuthProvider>
    </SiteContentProvider>
  </StrictMode>,
);
